import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { CommonRoute } from '../../../../common/enums/common-route.enum';
import { AppRefreshCoordinator } from '../../visibility/app-refresh-coordinator.service';
import { RefreshKey } from '../../visibility/refresh-key.enum';
import { ProGateService } from '../services/pro-gate.service';

/**
 * Blocks routes that exist only to sell (ADR-0173): the cart and the magic-link
 * checkout landing. When purchasing is switched off for this channel there is
 * nothing on those pages that may legally be shown — they are prices and payment
 * buttons end to end — so the user is sent home rather than shown an empty shell.
 *
 * Deliberately NOT applied to `/payment/callback` or
 * `/payment/checkout-confirmation`. Those are post-purchase return screens: a
 * buyer who paid moments before the switch flipped must still land somewhere that
 * confirms their purchase, and blocking them would strand a completed payment.
 */
export const purchaseEnabledGuard: CanActivateFn = () => {
  const proGate = inject(ProGateService);
  // Purchase availability is pulled by the surfaces that act on it (ADR-0264).
  // Not awaited: the decision uses the last verified answer, and the page's own
  // purchase controls follow the same signal if the fresh one differs.
  void inject(AppRefreshCoordinator).refreshIfStale(RefreshKey.PurchaseAvailability);
  if (proGate.purchasingEnabled()) return true;

  // Silent redirect, no toast: the user did not ask for this page in a way worth
  // explaining (a stale deep link or a leftover tab), and a message here would be
  // one more place to accidentally describe how to pay.
  return inject(Router).createUrlTree([CommonRoute.HOME]);
};
